import { type Interpolation, type Theme } from '@emotion/react';

import * as React from 'react';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import ChatLoader from 'bundles/common/components/ChatLoader';
import GlobalHelpButton from 'bundles/common/components/GlobalHelpButton';
import useChatWidgetSelector from 'bundles/page-header/components/useChatWidgetSelector';
import SignupSuccessAriaMessage from 'bundles/user-account/components/SignupSuccessAriaMessage';
import { useShowTermsOfUseModal } from 'bundles/user-consent/hooks/useShowTermsOfUseModal';

const TermsOfServiceModal = createLoadableComponent(
  () => import('bundles/user-consent/components/TermsOfServiceModal')
);
const AuthenticationOption = createLoadableComponent(
  () => import('bundles/user-account/components/AuthenticationOption')
);
const GoogleID = createLoadableComponent(() => import('bundles/authentication-common/components/GoogleID'));

type Props = {
  authModalOptions?: {
    disableAuth?: boolean;
    isEnterprise?: boolean;
    programId?: string;
    redirectOnCloseUrl?: string;
    utilizeWebRedirectOnClose?: boolean;
    preventRegister?: boolean;
    preventModalClose?: boolean;
  };
  chatButtonBottomOffsetOverride?: number;
  courseId?: string;
  floatingChatButtonContainerCssOverrides?: Interpolation<Theme>;
  hideChatWidget?: boolean;
  renderBoostChatOnSsr?: boolean;
};

// Enables highly critical functionality relevant to most pages. In the legacy page header these were included in the page header itself,
// but these features are not relevant to the page header ouside of the fact it was used globally across all apps and these features required that scope.
// This component serves to separate these features from the page header code and is intended to be included in each application when the new
// composable page header is used to ensure apps are not missing this critical functionality.
const UniversalPageFeatures = ({
  renderBoostChatOnSsr = true,
  authModalOptions,
  chatButtonBottomOffsetOverride,
  courseId,
  floatingChatButtonContainerCssOverrides,
  hideChatWidget,
}: Props) => {
  const location = useLocation();
  const userIsAuthenticated = isAuthenticatedUser();
  const {
    showTermsOfUseModal,
    setShowTermsOfUseModal,
    modalType,
    shouldShowModal: shouldLoadTermsOfUseModal,
  } = useShowTermsOfUseModal();
  const { chatWidgetToRender, boostChatConfig } = useChatWidgetSelector(courseId, { renderBoostChatOnSsr });
  const { enableChat, chatConfig, boostInstanceName, configValues } = boostChatConfig;
  const shouldLoadAuthModal =
    (location?.query?.showAuthModal || (location?.query?.authMode && !userIsAuthenticated)) &&
    !authModalOptions?.disableAuth;

  return (
    <>
      <SignupSuccessAriaMessage />
      {shouldLoadTermsOfUseModal && (
        <TermsOfServiceModal
          setShowTermsOfUseModal={setShowTermsOfUseModal}
          showTermsOfUseModal={showTermsOfUseModal}
          modalType={modalType}
        />
      )}
      {!userIsAuthenticated && <GoogleID />}
      {shouldLoadAuthModal && (
        <AuthenticationOption
          onCloseRedirectURL={authModalOptions?.redirectOnCloseUrl}
          utilizeWebRedirectOnClose={authModalOptions?.utilizeWebRedirectOnClose}
          preventModalClose={authModalOptions?.preventModalClose}
          preventRegister={authModalOptions?.preventRegister}
          key="authenticationOption"
          isEnterprise={authModalOptions?.isEnterprise}
          programId={authModalOptions?.programId}
        />
      )}
      {(chatWidgetToRender === 'BoostChat' || hideChatWidget) && (
        <ChatLoader
          chatButtonBottomOffsetOverride={chatButtonBottomOffsetOverride}
          floatingChatButtonContainerCssOverrides={floatingChatButtonContainerCssOverrides}
          hideFloatingButton={hideChatWidget || chatWidgetToRender !== 'BoostChat'}
          boostInstanceName={boostInstanceName}
          configValues={configValues}
          skipTargetingCheck
        />
      )}
      {!hideChatWidget && chatWidgetToRender === 'GlobalHelp' && (
        <GlobalHelpButton enableChat={enableChat} chatConfig={chatConfig} />
      )}
    </>
  );
};

export default UniversalPageFeatures;
